import { useAtom } from "jotai";
import { useInjection } from "../../di/DependencyContext";
import { AuthenticationService } from "./authentication.service";
import { DependencyType } from "../../di/DependencyType";
import React from "react";
import { currentUserStateAtom } from "~/state/currentUser";
import { syncSentryUser } from "./sentryUser";

export function useCurrentUser(clearCache?: boolean) {
  const [state, setState] = useAtom(currentUserStateAtom);

  const clearCacheRequestStateRef = React.useRef<
    "unset" | "started" | "deduped"
  >("unset");
  const lastClearCacheRef = React.useRef<boolean | undefined>(undefined);

  if (lastClearCacheRef.current !== clearCache) {
    lastClearCacheRef.current = clearCache;
    if (clearCache) {
      // `clearCache=true` means “force refresh once per mount / toggle”.
      clearCacheRequestStateRef.current = "unset";
    }
  }

  const authService = useInjection<AuthenticationService>(
    DependencyType.AuthenticationService
  );

  const cachedName = authService.getCachedFullname();
  const cachedRole = authService.getCachedRole();

  React.useEffect(() => {
    let requestId: number | undefined;
    let shouldFetch = false;

    setState((prev) => {
      if (!clearCache) {
        if (prev.status !== "idle") {
          return prev;
        }

        requestId = prev.requestId + 1;
        shouldFetch = true;
        return {
          ...prev,
          status: "loading",
          error: undefined,
          requestId,
        };
      }

      // `clearCache=true` should refresh once per hook mount.
      // If there's already a currentUser request in-flight, dedupe and use that result.
      if (clearCacheRequestStateRef.current !== "unset") {
        return prev;
      }

      if (prev.status === "loading") {
        clearCacheRequestStateRef.current = "deduped";
        return prev;
      }

      clearCacheRequestStateRef.current = "started";
      requestId = prev.requestId + 1;
      shouldFetch = true;
      return {
        ...prev,
        status: "loading",
        error: undefined,
        requestId,
      };
    });

    if (!shouldFetch || requestId === undefined) {
      return;
    }

    authService
      .me(clearCache)
      .then((user) => {
        syncSentryUser(user);
        setState((prev) => {
          if (prev.requestId !== requestId) {
            return prev;
          }
          return {
            status: "loaded",
            user,
            error: undefined,
            requestId: prev.requestId,
          };
        });
      })
      .catch((e) => {
        syncSentryUser(null);
        setState((prev) => {
          if (prev.requestId !== requestId) {
            return prev;
          }
          return {
            status: "error",
            user: undefined,
            error: e,
            requestId: prev.requestId,
          };
        });
      });
  }, [authService, clearCache, state.status]);

  const loading = state.status === "idle" || state.status === "loading";
  const currentUser = state.user
    ? {
        ...state.user,
        role: cachedRole ?? state.user.role,
      }
    : undefined;
  const error = state.error;

  return { currentUser, loading, error, cachedName, cachedRole };
}
